import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../utils/soundUtils';
import styled from 'styled-components';
import { Timestamp } from 'firebase/firestore';
import { findValidUser } from '../utils/firebaseUtils';
import backgroundImage from '../assets/images/bg-guide01.png';
import titleImage from '../assets/images/received-msg.png';
import popupBgImage from '../assets/images/popup-bg.png';
import scrollHeaderImage from '../assets/images/13message-scollHeader.png';
import scrollbarImage from '../assets/images/13message-scrollbar.png';
import receivedBtnImage from '../assets/images/received-btn.png';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: url(${backgroundImage}) center/cover no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  font-family: 'MapoDPP', sans-serif;
  color: white;
  overflow: hidden;
  z-index: 30001;
`;

const TitleImage = styled.img`
  width: 80%;
  max-width: 400px;
  height: auto;
  margin-top: 5%;
  margin-bottom: 2rem;
`;

const ScrollContainer = styled.div`
  position: relative;
  width: 95%;
  height: calc(100dvh - 35dvh);
  min-height: 40dvh;
  max-height: 60dvh;
  display: flex;
  margin-left: 5%;
  flex: 1;
  overflow: hidden;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.7rem;
  align-content: start;
  height: 100%;
  
  /* 기본 스크롤바 숨기기 */
  &::-webkit-scrollbar {
    width: 0px;
    display: none;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: transparent;
  }
  
  /* Firefox에서 스크롤바 숨기기 */
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  /* 모바일에서 터치 스크롤 최적화 */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
`;

const ScrollBar = styled.div`
  position: relative;
  width: 8dvw;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 5dvw;
  margin-bottom: 5dvw;
`;

const ScrollHeader = styled.div`
  width: 5dvw;
  height: 5dvw;
  flex-shrink: 0;
  position: absolute;
  top: 4dvw;
  right: 1.5dvw;
  z-index: 20;
`;

const ScrollHeaderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ScrollThumb = styled.div<{ top: number; height: number }>`
  position: absolute;
  right: 0;
  width: 100%;
  height: ${props => props.height}px;
  top: ${props => props.top}px;
  cursor: grab;
  transition: top 0.1s ease;
  z-index: 10;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const ScrollThumbImage = styled.img`
  width: 100%;
  object-fit: cover;
`;

const ScrollTrack = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  cursor: pointer;
  background: url(${scrollbarImage}) center/cover no-repeat;
  border-radius: 10px;
`;

const MessageContainer = styled.div<{ aspectRatio?: number }>`
  position: relative;
  width: 100%;
  height: ${props => props.aspectRatio ? `${82 / props.aspectRatio}vw` : '8dvw'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const MessageContainerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const MessageContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 5%;
`;

const MessageText = styled.div<{ aspectRatio?: number; textLength?: number }>`
  font-size: ${props => {
    if (!props.aspectRatio) return '0.4dvw';
    const baseSize = 56 / props.aspectRatio * 0.11;
    return props.textLength && props.textLength > 20
      ? `${baseSize * 0.85}vw` 
      : `${baseSize}vw`;
  }};
  color: white;
  text-align: center;
  font-family: 'MapoDPP', sans-serif;
  line-height: 1.4;
  word-break: keep-all;
  text-shadow: 0 0 25px #3884FF;
  margin-top: 2dvw;
`;

const BottomButton = styled.button`
  width: 80%;
  position: absolute;
  bottom: 5%;
  height: auto;
  margin-top: 2rem;
  margin-bottom: 2rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const BottomButtonImage = styled.img`
  width: 100%;
  height: auto;
`;

interface ReceivedMessage {
  message: string;
  senderNickname: string;
  receivedAt: Timestamp;
}

interface ReceivedMessagesProps {
  onClose: () => void;
}

const ReceivedMessages: React.FC<ReceivedMessagesProps> = ({ onClose }) => {
  const [receivedMessages, setReceivedMessages] = useState<ReceivedMessage[]>([]);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [scrollHeight, setScrollHeight] = useState<number>(0);
  const [clientHeight, setClientHeight] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartY, setDragStartY] = useState<number>(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState<number>(0);
  const [imageAspectRatio, setImageAspectRatio] = useState<number>(3);
  const scrollContentRef = useRef<HTMLDivElement>(null);

  // 이미지 비율 계산
  const calculateImageAspectRatio = () => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      setImageAspectRatio(ratio);
    };
    img.src = popupBgImage;
  };

  // 받은 메시지 가져오기
  const fetchReceivedMessages = async () => {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser) {
        return;
      }

      const userData = JSON.parse(currentUser);
      
      // 새로운 시간 기반 컬렉션 구조를 사용하여 사용자 데이터 가져오기
      const firebaseUserData = await findValidUser(userData.nickname, userData.avatarNumber);
      
      if (firebaseUserData) {
        const messages = firebaseUserData.receivedMessages || [];
        
        // 시간순으로 정렬 (최신 메시지가 위에)
        const sortedMessages = messages.sort((a: ReceivedMessage, b: ReceivedMessage) => {
          const timeA = a.receivedAt.toDate ? a.receivedAt.toDate() : new Date(a.receivedAt.seconds * 1000);
          const timeB = b.receivedAt.toDate ? b.receivedAt.toDate() : new Date(b.receivedAt.seconds * 1000);
          return timeB.getTime() - timeA.getTime();
        });
        
        setReceivedMessages(sortedMessages);
      } else {
        setReceivedMessages([]);
      }
    } catch (error) {
      console.error('받은 메시지 가져오기 오류:', error);
      setReceivedMessages([]);
    }
  };

  useEffect(() => {
    calculateImageAspectRatio();
    fetchReceivedMessages();
  }, []);

  // 초기 스크롤 상태 설정
  useEffect(() => {
    if (scrollContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContentRef.current;
      setScrollTop(scrollTop);
      setScrollHeight(scrollHeight);
      setClientHeight(clientHeight);
    }
  }, [receivedMessages]);

  const handleScroll = () => {
    if (scrollContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContentRef.current;
      setScrollTop(scrollTop);
      setScrollHeight(scrollHeight);
      setClientHeight(clientHeight);
    }
  };

  const handleScrollTrackClick = (e: React.MouseEvent) => {
    if (scrollContentRef.current && scrollHeight > clientHeight) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const trackHeight = rect.height;
      const scrollRatio = clickY / trackHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      const newScrollTop = scrollRatio * maxScrollTop;
      scrollContentRef.current.scrollTop = newScrollTop;
      setScrollTop(newScrollTop);
    }
  };

  // 스크롤바 썸의 위치와 높이 계산
  const getScrollThumbProps = () => {
    if (scrollHeight <= clientHeight) return { top: 0, height: 0 };
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = clientHeight - 10 * 2; // ScrollBar의 margin-top과 margin-bottom 제외
    const thumbHeight = Math.max(7 * 16, trackHeight * 0.2); // 최소 7dvw, 최대 trackHeight의 20%
    const top = scrollRatio * (trackHeight - thumbHeight);
    return { top, height: thumbHeight };
  };

  // 드래그 시작
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartScrollTop(scrollTop);
  };

  // 드래그 중
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollContentRef.current) return;
    
    const deltaY = e.clientY - dragStartY;
    const trackHeight = clientHeight - 80;
    const maxScrollTop = scrollHeight - clientHeight;
    
    const scrollRatio = deltaY / trackHeight;
    const newScrollTop = Math.max(0, Math.min(maxScrollTop, dragStartScrollTop + (scrollRatio * maxScrollTop)));
    
    scrollContentRef.current.scrollTop = newScrollTop;
    setScrollTop(newScrollTop);
  };

  // 드래그 종료
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 전역 마우스 이벤트 리스너 추가/제거
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragStartY, dragStartScrollTop, scrollHeight, clientHeight]);

  return (
    <Container>
      <TitleImage src={titleImage} alt="받은 메시지 타이틀" />
      
      <ScrollContainer>
        <ScrollContent 
          ref={scrollContentRef}
          onScroll={handleScroll}
        >
          {receivedMessages.length > 0 ? (
            receivedMessages.map((message, index) => (
              <MessageContainer 
                key={index} 
                aspectRatio={imageAspectRatio}
              >
                <MessageContainerImage 
                  src={popupBgImage} 
                  alt="메시지 컨테이너" 
                />
                <MessageContent>
                  <MessageText 
                    aspectRatio={imageAspectRatio} 
                    textLength={message.message.length}
                  >
                    [성좌, '{message.senderNickname}'이(가)<br />
                    {message.message?.replace(/^"|"$/g, '')}]
                  </MessageText>
                </MessageContent>
              </MessageContainer>
            ))
          ) : (
            <div style={{
              width: '100%',
              padding: '2rem',
              textAlign: 'center',
              color: 'white',
              fontSize: '1.2rem'
            }}>
              받은 메시지가 없습니다.
            </div>
          )}
        </ScrollContent>
        
        <ScrollBar>
          
          <ScrollTrack onClick={handleScrollTrackClick}>
            {(() => {
              const { top, height } = getScrollThumbProps();
              return height > 0 ? (
                <ScrollThumb 
                  top={top}
                  height={height}
                  onMouseDown={handleThumbMouseDown}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <ScrollThumbImage 
                    src={scrollHeaderImage} 
                    alt="스크롤 헤더"
                    draggable={false}
                  />
                </ScrollThumb>
              ) : null;
            })()}
          </ScrollTrack>
        </ScrollBar>
      </ScrollContainer>

      <BottomButton onClick={onClose}>
        <BottomButtonImage src={receivedBtnImage} alt="뒤로가기" />
      </BottomButton>
    </Container>
  );
};

export default ReceivedMessages;
